import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ATTENTION_STATUSES, getAttention, setAttentionDone, setAttentionStatus, type AttentionItem } from './crmApi'
import { IconAlert, IconBell, IconRefresh } from './crmUi'

/**
 * NEEDS YOUR ATTENTION dock (Lee, 22 Sep 2026) — floating widget, bottom-right
 * of every CRM screen, modelled on Lee's reference shots:
 *   · collapsed: a pill with the bell + "N waiting"
 *   · expanded: a floating card — header (refresh · dock-to-right · minimise),
 *     All/Open/Completed tabs, a per-company filter, and the item list
 *     (activity-down / churn-risk / CRM alerts). Circle = mark complete;
 *     row click opens the lead's full screen.
 *   · UNDOCKED the whole widget drags anywhere (grab the header / pill);
 *     "Dock to the right" pins it as a full-height right panel.
 * Position, open state and dock state persist in localStorage. Polls 60s.
 */

// v2 (22 Sep 2026): the widget is ANCHORED TO THE BOTTOM EDGE — it slides
// horizontally only (Lee: "movable but only across the bottom"), so the panel
// always opens UPWARD and can never leave the screen. Only x persists.
const LS = 'crm_attention_dock_v2'
type Persist = { open: boolean; docked: boolean; x: number | null }
const loadLS = (): Persist => { try { return { open: false, docked: false, x: null, ...JSON.parse(localStorage.getItem(LS) || '{}') } } catch { return { open: false, docked: false, x: null } } }
const saveLS = (p: Persist) => { try { localStorage.setItem(LS, JSON.stringify(p)) } catch { /* private mode */ } }
const EDGE = 20 // gap from the bottom/right edges

const KIND_CHIP: Record<string, { label: string; cls: string }> = {
  risk: { label: 'AT RISK', cls: 'bg-red-50 text-red-700 border-red-200' },
  trial: { label: 'TRIAL', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  call: { label: 'CALL', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  done_notice: { label: 'DONE', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  alert: { label: 'ALERT', cls: 'bg-surface-tertiary text-txt-secondary border-border' },
}

/** Status select + free note — shared working state with the Customer Success page. */
export const AttentionControls: React.FC<{ item: { key: string; status: string | null; note: string | null }; onChanged?: (patch: { status?: string | null; note?: string | null }) => void; compact?: boolean }> = ({ item, onChanged, compact }) => {
  const [note, setNote] = useState(item.note || '')
  useEffect(() => { setNote(item.note || '') }, [item.note])
  const saveStatus = (v: string) => {
    const status = v || null
    setAttentionStatus(item.key, { status }).catch(() => {})
    onChanged?.({ status })
  }
  const saveNote = () => {
    if ((note.trim() || null) === (item.note || null)) return
    setAttentionStatus(item.key, { note: note.trim() || null }).catch(() => {})
    onChanged?.({ note: note.trim() || null })
  }
  return (
    <span className={`flex items-center gap-1.5 ${compact ? '' : 'mt-1.5'}`} onClick={(e) => e.stopPropagation()}>
      <select
        value={item.status || ''}
        onChange={(e) => saveStatus(e.target.value)}
        className={`rounded-lg border bg-surface px-1.5 py-1 text-[10px] font-semibold focus:outline-none ${item.status ? 'border-brand-400 text-brand-700' : 'border-border text-txt-tertiary'}`}
      >
        <option value="">No action yet</option>
        {ATTENTION_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={saveNote}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
        placeholder="Add a note…"
        className="flex-1 min-w-0 rounded-lg border border-border bg-surface px-2 py-1 text-[10px] text-txt-primary focus:outline-none focus:border-brand-400"
      />
    </span>
  )
}

const fmtWhen = (iso: string) => new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })

export const CrmAttentionDock: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const location = useLocation()
  const navigate = useNavigate()
  // OPEN ONCE PER CALENDAR DAY (Lee, 29 Sep 2026 — replaced open-on-every-
  // arrival): the widget auto-expands only on the FIRST load of the day;
  // every later reload starts minimised. Manual open/close still works and
  // position/dock persist as before.
  const [p, setP] = useState<Persist>(() => {
    const today = new Date().toISOString().slice(0, 10)
    let firstOfDay = false
    try {
      if (localStorage.getItem('wq_attention_autoopen_day') !== today) {
        firstOfDay = true
        localStorage.setItem('wq_attention_autoopen_day', today)
      }
    } catch { /* private mode — behave like a repeat load */ }
    return { ...loadLS(), open: firstOfDay }
  })
  const [items, setItems] = useState<AttentionItem[]>([])
  // Category tabs (Lee, 23 Sep 2026): At Risk · Trials · Calls · Alerts ·
  // Actions Completed (finished-things notifications + everything ticked off).
  const [tab, setTab] = useState<'risk' | 'trial' | 'call' | 'alert' | 'completed'>('risk')
  const [companySel, setCompanySel] = useState('')
  const [companyOpen, setCompanyOpen] = useState(false)
  const [updatedAt, setUpdatedAt] = useState<number | null>(null)
  const dragRef = useRef<{ dx: number; dy: number } | null>(null)
  const boxRef = useRef<HTMLDivElement>(null)

  const persist = (next: Partial<Persist>) => setP((prev) => { const merged = { ...prev, ...next }; saveLS(merged); return merged })

  const load = (force = false) => getAttention(force).then((r) => { setItems(r.items); setUpdatedAt(Date.now()) }).catch(() => {})
  useEffect(() => { load(); const t = setInterval(() => load(), 60_000); return () => clearInterval(t) }, [])

  // Drag (undocked only): HORIZONTAL ONLY along the bottom edge — grab the
  // header or the collapsed pill. A real drag (>5px) suppresses the click, so
  // dragging the pill no longer pops the panel open (Lee, 22 Sep 2026).
  const draggedRef = useRef(false)
  const onDragStart = (e: React.PointerEvent) => {
    if (p.docked) return
    const box = boxRef.current?.getBoundingClientRect()
    if (!box) return
    draggedRef.current = false
    const startX = e.clientX
    dragRef.current = { dx: e.clientX - box.left, dy: 0 }
    const move = (ev: PointerEvent) => {
      if (!dragRef.current) return
      if (Math.abs(ev.clientX - startX) > 5) draggedRef.current = true
      if (!draggedRef.current) return
      const w = boxRef.current?.offsetWidth || 380
      const x = Math.min(Math.max(8, ev.clientX - dragRef.current.dx), window.innerWidth - w - 8)
      persist({ x })
    }
    const up = () => { dragRef.current = null; window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up) }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  const companies = useMemo(() => {
    const m = new Map<string, number>()
    items.forEach((i) => { if (i.dealName) m.set(i.dealName, (m.get(i.dealName) || 0) + 1) })
    return [...m.entries()].sort((a, b) => b[1] - a[1])
  }, [items])

  // Completed tab = the finished-things notifications (done_notice) plus any
  // item ticked off; the category tabs show their OPEN items only.
  const inTab = (i: AttentionItem, t: string) =>
    t === 'completed' ? (i.kind === 'done_notice' || i.done) : (i.kind === t && !i.done)
  const shown = useMemo(() => {
    let list = items.filter((i) => inTab(i, tab))
    if (companySel) list = list.filter((i) => i.dealName === companySel)
    return list
  }, [items, tab, companySel])

  const countOf = (t: string) => items.filter((i) => inTab(i, t)).length
  const openCount = items.filter((i) => !i.done && i.kind !== 'done_notice').length

  const toggleDone = (it: AttentionItem) => {
    setItems((prev) => prev.map((x) => (x.key === it.key ? { ...x, done: !it.done } : x)))
    setAttentionDone(it.key, !it.done).catch(() => load())
  }

  // CRM screens only — the support side keeps its own docks.
  if (!location.pathname.startsWith('/crm')) return null

  // Bottom-anchored, horizontal position only — the panel opens UPWARD from
  // here and is width-clamped, so it can never render off-screen.
  const clampX = (x: number, w: number) => Math.min(Math.max(8, x), Math.max(8, window.innerWidth - w - 8))
  const posStyle = p.docked
    ? undefined
    : p.x != null
      ? { left: clampX(p.x, p.open ? 400 : 180), bottom: EDGE }
      : { right: EDGE, bottom: EDGE }

  // ── collapsed pill (never when embedded in My Day) ──
  if (!p.open && !embedded) {
    return (
      <div ref={boxRef} className="fixed z-40" style={posStyle}>
        <button
          onPointerDown={onDragStart}
          onClick={() => { if (!draggedRef.current) persist({ open: true }) }}
          className="flex items-center gap-2.5 pl-3 pr-4 py-2.5 rounded-full bg-surface border border-border shadow-lg hover:shadow-xl cursor-grab active:cursor-grabbing"
          title="Needs Your Attention — click to open, drag left/right to move"
        >
          <span className={`inline-flex w-8 h-8 rounded-xl items-center justify-center ${openCount > 0 ? 'bg-red-50 text-red-600' : 'bg-surface-secondary text-txt-tertiary'}`}>
            <IconBell className="w-4 h-4" />
          </span>
          <span className="text-[13px] font-bold text-txt-primary tabular-nums">{openCount} waiting</span>
        </button>
      </div>
    )
  }

  // ── expanded ──
  // Floating panel is bottom-anchored (opens upward), height-capped to the
  // viewport minus the bottom gap — it structurally cannot overflow the screen.
  // EMBEDDED (Lee, 26 Oct 2026): inside My Day's tab — plain block, no
  // floating frame, no pill, no dock/minimise chrome.
  const frame = embedded
    ? 'h-full w-full bg-surface flex flex-col'
    : p.docked
      ? 'fixed inset-y-0 right-0 z-40 w-[400px] max-w-[92vw] bg-surface border-l border-border shadow-2xl flex flex-col'
      : 'fixed z-40 w-[400px] max-w-[92vw] max-h-[min(72vh,calc(100vh-40px))] bg-surface border border-border rounded-2xl shadow-2xl flex flex-col'

  return (
    <div ref={boxRef} className={frame} style={embedded ? undefined : posStyle}>
      {/* header — the drag handle when floating */}
      <div
        onPointerDown={onDragStart}
        className={`flex items-center gap-2 px-4 pt-3.5 pb-2 shrink-0 ${p.docked ? '' : 'cursor-grab active:cursor-grabbing'}`}
        title={p.docked ? undefined : 'Drag to move'}
      >
        <h3 className="text-[16px] font-bold text-txt-primary flex-1">Needs Your Attention</h3>
        {!embedded && (
          <button onClick={() => navigate('/crm/my-day?tab=attention')} className="px-2 py-1 rounded-lg text-[10px] font-bold text-brand-700 border border-brand-200 bg-brand-50 hover:bg-brand-100" title="Open My Day on the Needs-attention tab">
            Go to My Day →
          </button>
        )}
        <button onClick={() => load(true)} className="inline-flex w-7 h-7 items-center justify-center rounded-lg text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover" title="Refresh">
          <IconRefresh className="w-3.5 h-3.5" />
        </button>
        {!embedded && <>
        <button
          onClick={() => persist({ docked: !p.docked })}
          className={`inline-flex w-7 h-7 items-center justify-center rounded-lg hover:bg-surface-hover ${p.docked ? 'text-brand-600 bg-brand-50' : 'text-txt-tertiary hover:text-txt-primary'}`}
          title={p.docked ? 'Undock (float)' : 'Dock to the right'}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M15 4v16" /></svg>
        </button>
        <button onClick={() => persist({ open: false })} className="inline-flex w-7 h-7 items-center justify-center rounded-lg text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover" title="Minimise">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" className="w-3.5 h-3.5"><path d="M5 12h14" /></svg>
        </button>
        </>}
      </div>
      <p className="px-4 text-[11px] text-txt-tertiary shrink-0">
        {items.length} item{items.length === 1 ? '' : 's'}{updatedAt ? ` · updated ${Math.max(0, Math.round((Date.now() - updatedAt) / 1000)) < 5 ? 'just now' : new Date(updatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : ''}
      </p>

      {/* category tabs + company filter */}
      <div className="flex items-center gap-2.5 px-4 pt-2.5 pb-2 border-b border-border shrink-0 overflow-x-auto">
        {([['risk', 'At Risk'], ['trial', 'Trials'], ['call', 'Calls'], ['alert', 'Alerts'], ['completed', 'Completed']] as const).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`text-[12px] font-semibold pb-1 border-b-2 whitespace-nowrap shrink-0 ${tab === k ? (k === 'risk' ? 'text-red-600 border-red-500' : 'text-txt-primary border-txt-primary') : 'text-txt-tertiary border-transparent hover:text-txt-primary'}`}
          >
            {label} <span className="font-normal tabular-nums">{countOf(k)}</span>
          </button>
        ))}
        <span className="ml-auto relative">
          <button onClick={() => setCompanyOpen((o) => !o)} className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${companySel ? 'border-red-300 bg-red-50 text-red-600' : 'border-border text-txt-secondary hover:bg-surface-hover'}`}>
            {companySel || 'All records'}
          </button>
          {companyOpen && (
            <span className="absolute right-0 top-8 z-50 w-60 max-h-64 overflow-y-auto rounded-xl border border-border bg-surface shadow-lg py-1.5 block">
              <button onClick={() => { setCompanySel(''); setCompanyOpen(false) }} className={`w-full flex justify-between px-3.5 py-1.5 text-[12px] hover:bg-surface-hover ${!companySel ? 'text-red-600 font-semibold bg-red-50/60' : 'text-txt-primary'}`}>
                All records <span className="tabular-nums">{items.length}</span>
              </button>
              {companies.map(([name, n]) => (
                <button key={name} onClick={() => { setCompanySel(name); setCompanyOpen(false) }} className={`w-full flex justify-between gap-2 px-3.5 py-1.5 text-[12px] hover:bg-surface-hover ${companySel === name ? 'text-red-600 font-semibold' : 'text-txt-primary'}`}>
                  <span className="truncate text-left">{name}</span><span className="tabular-nums shrink-0">{n}</span>
                </button>
              ))}
            </span>
          )}
        </span>
      </div>

      {/* items */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {shown.length === 0 ? (
          <p className="text-[12px] text-txt-tertiary text-center py-10">
            {tab === 'completed' ? 'Nothing completed yet.' : 'Nothing needs your attention here. Nice.'}
          </p>
        ) : shown.map((it) => {
          const chip = KIND_CHIP[it.kind] || KIND_CHIP.alert
          // Completed-tab material is READ-ONLY (Lee, 29 Sep 2026): no
          // tick circle, no status/note controls — it's a record, not work.
          const readOnly = it.kind === 'done_notice' || it.done
          return (
            <div
              key={it.key}
              onClick={() => { if (it.dealId) navigate(`/crm/lead/${it.dealId}`) }}
              className={`flex items-start gap-2.5 px-4 py-3 border-b border-border last:border-0 ${it.dealId ? 'cursor-pointer hover:bg-surface-hover' : ''} ${it.done ? 'opacity-60' : ''}`}
            >
              {readOnly ? (
                <span className="mt-0.5 w-[18px] h-[18px] rounded-full shrink-0 inline-flex items-center justify-center bg-emerald-500 text-white">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" className="w-2.5 h-2.5"><path d="M20 6 9 17l-5-5" /></svg>
                </span>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); toggleDone(it) }}
                  className="mt-0.5 w-[18px] h-[18px] rounded-full border-2 shrink-0 inline-flex items-center justify-center border-txt-tertiary/40 hover:border-emerald-500"
                  title="Mark complete"
                />
              )}
              <div className="min-w-0 flex-1">
                <p className={`text-[13px] font-bold text-txt-primary ${it.done ? 'line-through' : ''}`}>{it.title}</p>
                <p className="text-[11px] mt-0.5 flex items-center gap-1.5 flex-wrap">
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${chip.cls}`}>{chip.label}</span>
                  {it.severity === 'critical' && <span className="inline-flex items-center gap-0.5 text-red-600 font-bold"><IconAlert className="w-3 h-3" /> Critical</span>}
                  <span className="text-txt-tertiary">{fmtWhen(it.at)}</span>
                </p>
                {it.detail && <p className="text-[11px] text-txt-secondary mt-0.5 line-clamp-2">{it.detail}</p>}
                {it.dealName && <p className="text-[11px] text-txt-tertiary mt-0.5 truncate">{it.dealName}</p>}
                {!readOnly && (
                  <AttentionControls
                    item={it}
                    onChanged={(patch) => setItems((prev) => prev.map((x) => (x.key === it.key ? { ...x, ...patch, done: patch.status === 'resolved' ? true : x.done } : x)))}
                  />
                )}
              </div>
              {it.dealId && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-txt-tertiary shrink-0 mt-1"><path d="m9 18 6-6-6-6" /></svg>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
